import Phaser from "phaser";
import { unlockAudio } from "../audio/sfx";
import { t } from "../data/strings";
import { SceneKey, assertTransition } from "../flow/game-flow";
import { sharedInput } from "../input/input-controller";
import { getUiLayout } from "../ui/layout";
import { clearOverlay } from "../ui/screen-overlay";
import { markActiveScene } from "../ui/screen-state";
import {
  mountRaidWikiPanel,
  type RaidWikiPanelApi,
} from "../ui/huds/raid/wiki/wiki-panel";

/**
 * Raid entry hub — Title ↔ Hub → Sim Lab | Roster | Wiki → Battle.
 */
export class RaidHubScene extends Phaser.Scene {
  private disposed = false;
  private wikiApi: RaidWikiPanelApi | null = null;

  constructor() {
    super(SceneKey.RaidHub);
  }

  create(): void {
    this.disposed = false;
    this.wikiApi = null;
    markActiveScene(SceneKey.RaidHub);
    sharedInput.attach();
    clearOverlay();

    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor("#0c1424");
    this.add
      .rectangle(width / 2, height / 2, width, height, 0x0c1424)
      .setDepth(-1);
    this.add
      .text(width / 2, height * 0.12, t("raid.hub.title"), {
        fontFamily: "Malgun Gothic, sans-serif",
        fontSize: "28px",
        color: "#ffd27a",
      })
      .setOrigin(0.5);
    this.add
      .text(width / 2, height * 0.175, t("raid.hub.subtitle"), {
        fontFamily: "Malgun Gothic, sans-serif",
        fontSize: "13px",
        color: "#9aa3c7",
      })
      .setOrigin(0.5);

    this.buildMenuDom();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
  }

  update(): void {
    if (this.disposed) return;
    const pressed = sharedInput.consumePressed();
    if (pressed.cancel) {
      if (this.wikiApi) {
        this.closeWiki();
        return;
      }
      this.backToTitle();
    }
  }

  private disposeWiki(): void {
    this.wikiApi?.dispose();
    this.wikiApi = null;
  }

  private prepareFrame(): HTMLElement {
    const ui = getUiLayout();
    ui.sync();
    // Wiki lives on #screen-overlay (sibling of SafeFrame) — dispose before menu shell.
    this.disposeWiki();
    ui.clearShell();
    const frame = ui.frame;
    frame.style.display = "flex";
    frame.style.flexDirection = "column";
    frame.style.alignItems = "center";
    frame.style.justifyContent = "center";
    frame.style.pointerEvents = "auto";
    frame.style.padding = "";
    frame.style.overflow = "";
    frame.style.minHeight = "";
    ui.overlay.style.pointerEvents = "auto";
    return frame;
  }

  private buildMenuDom(): void {
    const frame = this.prepareFrame();

    const panel = document.createElement("div");
    panel.className = "menu-panel raid-hub-panel";
    panel.innerHTML = `
      <p class="raid-hub-hint" data-hint></p>
      <div class="menu-row">
        <button type="button" class="menu-btn" data-sim></button>
      </div>
      <div class="menu-row">
        <button type="button" class="menu-btn" data-roster></button>
      </div>
      <div class="menu-row">
        <button type="button" class="menu-btn" data-wiki></button>
      </div>
      <div class="menu-row">
        <button type="button" class="menu-btn" data-back></button>
      </div>
    `;
    frame.appendChild(panel);

    panel.querySelector("[data-hint]")!.textContent = t("raid.hub.hint");
    const simBtn = panel.querySelector("[data-sim]") as HTMLButtonElement;
    const rosterBtn = panel.querySelector("[data-roster]") as HTMLButtonElement;
    const wikiBtn = panel.querySelector("[data-wiki]") as HTMLButtonElement;
    const backBtn = panel.querySelector("[data-back]") as HTMLButtonElement;
    simBtn.textContent = t("raid.hub.toSim");
    rosterBtn.textContent = t("raid.hub.toRoster");
    wikiBtn.textContent = t("raid.hub.wiki");
    backBtn.textContent = t("raid.hub.back");

    simBtn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      this.goSim();
    });
    rosterBtn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      this.goRoster();
    });
    wikiBtn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      this.openWiki();
    });
    backBtn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      this.backToTitle();
    });
  }

  private openWiki(): void {
    if (this.disposed) return;
    unlockAudio();
    this.disposeWiki();
    const ui = getUiLayout();
    ui.sync();
    ui.clearShell();
    // Sim Lab pattern: full #screen-overlay, not FIT letterbox SafeFrame.
    // Desktop would otherwise stay phone-width inside the portrait canvas rect.
    const frame = ui.frame;
    frame.style.display = "";
    frame.style.pointerEvents = "none";
    frame.style.padding = "";
    frame.style.overflow = "";
    frame.style.minHeight = "";
    ui.overlay.style.pointerEvents = "auto";
    this.wikiApi = mountRaidWikiPanel({
      host: ui.overlay,
      onBack: () => this.closeWiki(),
    });
  }

  private closeWiki(): void {
    if (this.disposed) return;
    this.disposeWiki();
    this.buildMenuDom();
  }

  private goSim(): void {
    if (this.disposed) return;
    unlockAudio();
    assertTransition(SceneKey.RaidHub, SceneKey.RaidSim);
    this.scene.start(SceneKey.RaidSim);
  }

  private goRoster(): void {
    if (this.disposed) return;
    unlockAudio();
    assertTransition(SceneKey.RaidHub, SceneKey.RaidRoster);
    this.scene.start(SceneKey.RaidRoster);
  }

  private backToTitle(): void {
    if (this.disposed) return;
    assertTransition(SceneKey.RaidHub, SceneKey.Title);
    this.scene.start(SceneKey.Title);
  }

  private teardown(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.disposeWiki();
    getUiLayout().clearShell();
  }
}
